/**
 * Voorzetramen (Timmers STORM_WINDOW) — prijs precies zoals de Timmers-webshop.
 *
 * De Timmers-consumentenprijs is géén simpele matrix-lookup, maar:
 *   1. rond breedte/hoogte omhoog naar de eerstvolgende rastermaat STRIKT GROTER
 *      (invoer 1000 → cel 1100; invoer 400 → cel 500). De 2000-breedte is het maximum,
 *      hoogtes lopen door tot 3000 — dat verklaart de "asymmetrie" (2000×400 ≠ 400×2000).
 *   2. basisprijs = matrixcel × 0,75 (25% korting op de Timmers-lijstprijs).
 *   3. + "grootte toeslag" over het NOMINALE oppervlak: +50% vanaf 3 m², +100% vanaf 6 m².
 *   4. + energietoeslag (verwijderingsbijdrage) = oppervlak × dikte × 2,5 kg/m²/mm × €0,037/kg.
 * Totaal (excl. btw) = basis × (1 + grootte%/100) + energietoeslag.
 *
 * Belangrijk: de toeslagen hangen van het NOMINALE oppervlak af (niet de rastermaat), dus
 * de opgeslagen `priceCents` per cel is alleen de BASIS; de toeslagen worden bij het prijzen
 * uit de werkelijke maat berekend. Eén bron, gedeeld door client (productpagina/winkelwagen)
 * en server (createOrder — prijsautoriteit). Geverifieerd tegen de live Timmers-site (t/m ~1 cent,
 * de restafwijking is Timmers' eigen 2-decimalen-afronding in de matrix).
 */

/** Timmers-producttype van voorzetramen. */
export const STORM_WINDOW_TYPE = "STORM_WINDOW";
/** Korting op de Timmers-lijst(matrix)prijs → consumentenbasisprijs. */
export const STORM_WINDOW_DISCOUNT_PCT = 25;
/** Grootte-toeslag-staffel (over nominaal oppervlak, m²). Uit allowances.json. */
export const STORM_SURFACE_ALLOWANCES: ReadonlyArray<{ surfaceM2: number; pct: number }> = [
  { surfaceM2: 3, pct: 50 },
  { surfaceM2: 6, pct: 100 },
];
/** Energietoeslag (verwijderingsbijdrage) in €/kg. Uit settings: energyAllowance. */
export const STORM_ENERGY_RATE_PER_KG = 0.037;
/** Standaard glasgewicht (kg per m² per mm dikte). */
export const STORM_WEIGHT_PER_M2_PER_MM = 2.5;

/**
 * Uitvoeringsopties voor voorzetramen (géén prijsinvloed — alle keuzes kosten hetzelfde;
 * gaan als spec mee op de order). Kleuren per productlijn: QualiView heeft een vaste RAL-set
 * (alleen standaard, géén "op aanvraag"); WoodView wordt op kleur geschilderd → geen vaste set.
 */
export interface StormColor {
  /** Volledige naam voor op de order, bv. "Wit (RAL 9010)". */
  name: string;
  /** RAL-code voor de swatch-kleur (leeg = eigen hex, bv. Aluminium F1). */
  ral?: string;
  /** Swatch-achtergrond als er geen RAL-code is (CSS-kleur of gradient). */
  hex?: string;
}
export const STORM_QUALIVIEW_COLORS: readonly StormColor[] = [
  { name: "Wit (RAL 9010)", ral: "9010" },
  { name: "Crème (RAL 9001)", ral: "9001" },
  { name: "Groen (RAL 6009)", ral: "6009" },
  { name: "Bruin (RAL 8019)", ral: "8019" },
  { name: "Aluminium (F1)", hex: "linear-gradient(135deg,#dcdde0,#a2a4a7)" },
];
/** Bevestiging: geschroefd of met knevels, met of zonder scharnieren (alleen QualiView). */
export const STORM_MOUNTINGS = [
  "Geschroefd met scharnieren",
  "Knevels met scharnieren",
  "Geschroefd zonder scharnieren",
  "Knevels zonder scharnieren",
] as const;
/** Scharnierzijde: aan welke zijde de scharnieren zitten (alleen bij bevestiging mét scharnieren). */
export const STORM_HINGE_SIDES = ["Links", "Rechts", "Boven", "Onder"] as const;

/** Standaardkleuren voor een voorzetraam-slug (leeg = geen vaste kleurkeuze, bv. WoodView). */
export function stormColorsFor(slug: string): readonly StormColor[] {
  return slug.startsWith("qualiview-voorzetraam") ? STORM_QUALIVIEW_COLORS : [];
}
/**
 * Vaste (niet-kiesbare) uitvoering. WoodView heeft altijd een RAL 1013-rand en wordt altijd
 * geschroefd (geen kleur-/bevestigingskeuze). QualiView → null (je kiest zelf). */
export function stormFixedFrame(slug: string): { ral: string; mounting: string } | null {
  return slug.startsWith("woodview-voorzetraam") ? { ral: "1013", mounting: "Geschroefd" } : null;
}
/** Bevestigingsopties voor een voorzetraam-slug (alleen QualiView; WoodView heeft ze niet). */
export function stormMountingsFor(slug: string): readonly string[] {
  return slug.startsWith("qualiview-voorzetraam") ? STORM_MOUNTINGS : [];
}
/** Heeft deze bevestiging scharnieren? Zo niet, dan vervalt de scharnierzijde-keuze. */
export function stormMountingHasHinges(mounting: string): boolean {
  return !/zonder/i.test(mounting);
}

/** Kleinste rastermaat STRIKT groter dan `x`; anders de grootste (maximum) rastermaat. */
export function stormGridUp(x: number, grid: readonly number[]): number {
  for (const v of grid) if (x < v) return v;
  return grid[grid.length - 1] ?? x;
}

/** Grootte-toeslag-percentage voor een nominaal oppervlak (m²). */
export function stormSurchargePct(areaM2: number): number {
  let pct = 0;
  for (const t of STORM_SURFACE_ALLOWANCES) if (areaM2 >= t.surfaceM2) pct = t.pct;
  return pct;
}

/**
 * Eindprijs (cents, excl. btw) voor een voorzetraam.
 * @param baseCents  basisprijs van de gekozen rastercel (matrix × 0,75), al opgeslagen.
 * @param wMm,hMm    NOMINALE maat (klantinvoer) — bepaalt oppervlak voor de toeslagen.
 * @param thicknessMm glasdikte (33.1 gelaagd = 6) voor de energietoeslag.
 */
export interface StormBreakdown {
  baseCents: number;
  /** Grootte-toeslag-percentage (0/50/100) voor de gekozen maat. */
  surchargePct: number;
  surchargeCents: number;
  energyCents: number;
  totalCents: number;
}

/** Volledige prijsopbouw van een voorzetraam (voor de "Controle"-uitsplitsing). */
export function stormWindowBreakdown(baseCents: number, wMm: number, hMm: number, thicknessMm: number): StormBreakdown {
  const areaM2 = (wMm * hMm) / 1e6;
  const surchargePct = stormSurchargePct(areaM2);
  const surchargeCents = Math.round((baseCents * surchargePct) / 100);
  const energyCents = Math.round(areaM2 * thicknessMm * STORM_WEIGHT_PER_M2_PER_MM * STORM_ENERGY_RATE_PER_KG * 100);
  return { baseCents, surchargePct, surchargeCents, energyCents, totalCents: baseCents + surchargeCents + energyCents };
}

export function stormWindowPriceCents(baseCents: number, wMm: number, hMm: number, thicknessMm: number): number {
  return stormWindowBreakdown(baseCents, wMm, hMm, thicknessMm).totalCents;
}
